<template>
  <div class="c-job-price-amount-fields">
    <CFlex justify="between" align="end">
      <CLabel v-tooltip="$t('jobApproval.clientPriceDescription')">
        {{ $t('jobApproval.clientPrice') }}
        <CIcon name="info" :size="20" class="u-mb-1"/>
      </CLabel>

      <CButton v-if="canChangePrice && job.price.dmsAmountWithoutVat !== null" theme="ghost-primary" @click="copyFromDms">
        <CFlex justify="between">
          {{ $t('jobApproval.copyDmsPrice') }}
          <CIcon name="copy"/>
        </CFlex>
      </CButton>
    </CFlex>

    <CCard class="c-job-price-amount-fields-container">
      <CFlex>
        <div>
          <CFlex align="center">
            <CField type="amount" v-model="amountWithVat" :disabled="!canChangePrice" @input="calculateAmountWithoutVatDebounced" class="u-m-0"/>
            <p>{{ $t('jobApproval.vatIncl', { rate: formatPercentage(job.price.vatRate) }) }} </p>
          </CFlex>
          <CFlex align="center" class="u-mt-2">
            <CField type="amount" v-model="amountWithoutVat" :disabled="!canChangePrice" @input="calculateAmountWithVatDebounced" class="u-m-0"/>
            <p>{{ $t('jobApproval.vatExcl', { rate: formatPercentage(job.price.vatRate) }) }}</p>
          </CFlex>
        </div>

        <div>
          <CLabel>{{ $t('jobApproval.invoicing') }}</CLabel>
          <CDropdown class="c-job-approval-table-warranty">
            <template #icon>
              <CWarranty :status="warrantyStatus"/>
            </template>

            <CButton v-for="status in enumValues(WarrantyStatus)" @click="updateWarranty(status)">
              <CWarranty :status/>
            </CButton>
          </CDropdown>
        </div>
      </CFlex>
    </CCard>

    <CCard v-if="deviation !== 0" theme="warning" class="u-semibold u-mt-2">
      <CColor theme="warning">
        <CIcon name="warning-circle"/>
        {{ $t('jobApproval.priceDeviationWarning', { deviation: formatAmount(deviation) }) }}
      </CColor>
    </CCard>
  </div>
</template>

<script setup lang="ts">
  import { debounce } from 'lodash';
  import { computed } from 'vue';
  import { formatAmount, formatPercentage } from '@/shared/utils/format';
  import CField from '@/shared/components/form/CField.vue';
  import CCard from '@/shared/components/CCard.vue';
  import CColor from '@/shared/components/CColor.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import CFlex from '@/shared/components/CFlex.vue';
  import CLabel from '@/shared/components/form/CLabel.vue';
  import { addRate, roundAmount, subtractRate } from '@/general/utils/amount';
  import Job from '@/jobs/models/Job';
  import CDropdown from '@/shared/components/dropdown/CDropdown.vue';
  import { enumValues, isAnyOf } from '@/shared/utils/typescript';
  import WarrantyStatus from '@/jobs/constants/WarrantyStatus';
  import CWarranty from '@/jobs/components/CWarranty.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';

  const props = defineProps<{
    job: Job;
  }>();

  const amountWithVat = defineModel<number | null>('amountWithVat', { default: null });
  const amountWithoutVat = defineModel<number | null>('amountWithoutVat', { default: null });
  const warrantyStatus = defineModel<WarrantyStatus>('warrantyStatus', { default: WarrantyStatus.NO_WARRANTY });

  const canChangePrice = computed(() => {
    return isAnyOf(warrantyStatus.value, WarrantyStatus.NO_WARRANTY, WarrantyStatus.PARTIAL);
  });

  const deviation = computed(() => {
    if (amountWithVat.value === null || props.job.price.dmsAmountWithVat === null || !canChangePrice.value) return 0;
    return roundAmount(amountWithVat.value - props.job.price.dmsAmountWithVat);
  });

  const calculateAmountWithVatDebounced = debounce(calculateAmountWithVat, 500);
  const calculateAmountWithoutVatDebounced = debounce(calculateAmountWithoutVat, 500);

  function calculateAmountWithVat() {
    if (props.job.price.vatRate !== null && amountWithoutVat.value !== null) {
      amountWithVat.value = addRate(amountWithoutVat.value, props.job.price.vatRate);
    } else {
      amountWithVat.value = null;
    }
  }

  function calculateAmountWithoutVat() {
    if (props.job.price.vatRate !== null && amountWithVat.value !== null) {
      amountWithoutVat.value = subtractRate(amountWithVat.value, props.job.price.vatRate);
    } else {
      amountWithoutVat.value = null;
    }
  }

  function copyFromDms() {
    amountWithVat.value  = props.job.price.dmsAmountWithVat;
    amountWithoutVat.value  = props.job.price.dmsAmountWithoutVat;
  }

  function updateWarranty(status: WarrantyStatus): void {
    const job = props.job;
    warrantyStatus.value = status;

    if (status === WarrantyStatus.WARRANTY) {
      amountWithoutVat.value = 0;
      amountWithVat.value = 0;
    } else if (isAnyOf(status, WarrantyStatus.PARTIAL, WarrantyStatus.NO_WARRANTY)) {
      amountWithoutVat.value = job.price.amountWithoutVat ?? null;
      amountWithVat.value = job.price.amountWithVat ?? null;
    } else {
      amountWithoutVat.value = null;
      amountWithVat.value = null;
    }
  }
</script>

<style lang="scss">
.c-job-price-amount-fields {
  &-container > .c-flex {
    gap: 0;

    & > div:nth-child(1) {
      padding-right: 2rem;
      border-right: 1px solid $border-color;
    }

    & > div:nth-child(2) {
      padding-left: 2rem;
    }
  }
}
</style>